import React, { useState, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
  updateDoc,
  serverTimestamp,
  addDoc,
  deleteDoc,
  arrayUnion,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebase';
import { useAuth } from '../../components/AuthContext';
import logClientActivity from '../../utils/logClientActivity';
import {
  InformationCircleIcon,
  TrashIcon,
  PlusIcon,
  PaperAirplaneIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilSquareIcon,
  ChatBubbleLeftEllipsisIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/solid';

const STATUS_STYLES = {
  pendiente: 'bg-yellow-100 text-yellow-800',
  enviado: 'bg-blue-100 text-blue-800',
  aceptado: 'bg-green-100 text-green-800',
  aceptada: 'bg-green-100 text-green-800',
  rechazado: 'bg-red-100 text-red-800',
  rechazada: 'bg-red-100 text-red-800',
  convertido: 'bg-gray-200 text-gray-600',
};

const STATUS_LABELS = {
  pendiente: 'Pendiente',
  enviado: 'Enviado',
  aceptado: 'Aceptado',
  aceptada: 'Aceptada',
  rechazado: 'Rechazado',
  rechazada: 'Rechazada',
  convertido: 'Convertida a factura',
};

const emptyItem = { name: '', description: '', quantity: 1, price: '' };
const PORTAL_LOGIN_URL = 'https://bbtspr.com/login';

const getProposalNotes = (proposal = {}) =>
  String((proposal?.clientNotes) || (proposal?.notes) || '').trim();

const getProposalImages = (proposal = {}) => {
  if (!proposal || !Array.isArray(proposal.images)) {
    return [];
  }

  return Array.from(
    new Set(
      proposal.images
        .map((imageUrl) => String(imageUrl || '').trim())
        .filter(Boolean)
    )
  );
};

const parseImageUrlsInput = (value = '') =>
  Array.from(
    new Set(
      String(value || '')
        .split(/\r?\n|,/)
        .map((imageUrl) => imageUrl.trim())
        .filter(Boolean)
    )
  );

const buildProposalHtml = (proposal, client) => {
  const createdAt = proposal.createdAt?.toDate?.() || (proposal.createdAt ? new Date(proposal.createdAt) : new Date());
  const issueDate = createdAt.toLocaleDateString('es-PR', { year: 'numeric', month: 'long', day: 'numeric' });
  const totalValue = parseFloat(proposal.total || 0);
  const depositValue = parseFloat(proposal.deposit || 0);
  const remainingValue = proposal.remaining !== undefined ? parseFloat(proposal.remaining) : totalValue - depositValue;
  const proposalNotes = getProposalNotes(proposal);
  const proposalImages = getProposalImages(proposal);
  const portalAccessUrl = proposal.portalLoginUrl || PORTAL_LOGIN_URL;

  const itemsHtml = (proposal.items || []).map(item => {
    const subtotal = (parseFloat(item.quantity || 0) * parseFloat(item.price || 0)).toFixed(2);
    return `
      <tr>
        <td style="padding:10px 12px;border:1px solid #e5e7eb;">${item.name || ''}</td>
        <td style="padding:10px 12px;border:1px solid #e5e7eb;color:#4b5563;">${item.description || ''}</td>
        <td style="padding:10px 12px;border:1px solid #e5e7eb;text-align:center;">${item.quantity || 1}</td>
        <td style="padding:10px 12px;border:1px solid #e5e7eb;text-align:right;">$${parseFloat(item.price || 0).toFixed(2)}</td>
        <td style="padding:10px 12px;border:1px solid #e5e7eb;text-align:right;font-weight:600;">$${subtotal}</td>
      </tr>`;
  }).join('');

  const notesHtml = `<div style="margin-top:24px;background:#fffbeb;border:1px solid #fcd34d;border-radius:8px;padding:14px 18px;">
      <p style="margin:0 0 6px;font-size:12px;font-weight:700;text-transform:uppercase;color:#92400e;">Notas de la propuesta</p>
      <p style="margin:0;font-size:13px;color:#1f2937;white-space:pre-line;">${proposalNotes || 'Sin notas registradas para esta propuesta.'}</p>
     </div>`;

  const imagesHtml = proposalImages.length
    ? `<div style="margin-top:24px;">
        <p style="margin:0 0 10px;font-size:12px;font-weight:700;text-transform:uppercase;color:#374151;">Imágenes de referencia</p>
        <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;">
          ${proposalImages.map((imageUrl, index) => `
            <div style="border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;background:#f9fafb;">
              <img src="${imageUrl}" alt="Imagen de propuesta ${index + 1}" style="display:block;width:100%;height:220px;object-fit:cover;" />
            </div>
          `).join('')}
        </div>
      </div>`
    : '';

  return `
    <div style="font-family:'Segoe UI',Arial,sans-serif;max-width:800px;margin:auto;color:#1f2937;padding:24px;">
      <div style="display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #f97316;padding-bottom:16px;margin-bottom:24px;">
        <div>
          <p style="margin:0;font-size:11px;letter-spacing:0.15em;text-transform:uppercase;color:#6b7280;">Propuesta Comercial</p>
          <h1 style="margin:4px 0 0;font-size:26px;font-weight:800;color:#111827;">Carlo Rivera DBA BBTS</h1>
        </div>
        <div style="text-align:right;">
          <p style="margin:0;font-size:12px;color:#6b7280;">${issueDate}</p>
          <p style="margin:4px 0 0;font-size:12px;font-weight:600;color:#f97316;">#${proposal.id?.slice(0, 8).toUpperCase() || 'N/A'}</p>
        </div>
      </div>

      <div style="display:flex;gap:16px;margin-bottom:28px;">
        <div style="flex:1;background:#f9fafb;border-radius:10px;padding:14px 18px;">
          <p style="margin:0 0 6px;font-size:11px;text-transform:uppercase;letter-spacing:0.12em;color:#6b7280;">Cliente</p>
          <p style="margin:0;font-size:17px;font-weight:700;">${client?.name || proposal.clientInfo?.name || 'N/A'}</p>
          <p style="margin:4px 0 0;font-size:13px;color:#4b5563;">${proposal.clientInfo?.email || ''}</p>
          <p style="margin:2px 0 0;font-size:13px;color:#4b5563;">${proposal.clientInfo?.phone || ''}</p>
        </div>
        <div style="flex:1;background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:14px 18px;text-align:right;">
          <p style="margin:0 0 4px;font-size:11px;text-transform:uppercase;letter-spacing:0.12em;color:#9a3412;">Total propuesta</p>
          <p style="margin:0;font-size:28px;font-weight:800;color:#c2410c;">$${totalValue.toFixed(2)}</p>
          <p style="margin:4px 0 0;font-size:12px;color:#7c3aed;">Depósito: $${depositValue.toFixed(2)}</p>
          <p style="margin:2px 0 0;font-size:12px;color:#6b7280;">Saldo: $${remainingValue.toFixed(2)}</p>
        </div>
      </div>

      <h3 style="font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:#374151;margin-bottom:10px;">Servicios</h3>
      <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:8px;">
        <thead>
          <tr style="background:#f97316;color:#fff;">
            <th style="padding:10px 12px;text-align:left;border:1px solid #f97316;">Servicio</th>
            <th style="padding:10px 12px;text-align:left;border:1px solid #f97316;">Descripción</th>
            <th style="padding:10px 12px;text-align:center;border:1px solid #f97316;">Cant.</th>
            <th style="padding:10px 12px;text-align:right;border:1px solid #f97316;">Precio</th>
            <th style="padding:10px 12px;text-align:right;border:1px solid #f97316;">Total</th>
          </tr>
        </thead>
        <tbody>${itemsHtml}</tbody>
        <tfoot>
          <tr style="background:#f9fafb;">
            <td colspan="4" style="padding:10px 12px;border:1px solid #e5e7eb;text-align:right;font-weight:700;">Total</td>
            <td style="padding:10px 12px;border:1px solid #e5e7eb;text-align:right;font-weight:700;">$${totalValue.toFixed(2)}</td>
          </tr>
          <tr>
            <td colspan="4" style="padding:10px 12px;border:1px solid #e5e7eb;text-align:right;">Depósito requerido</td>
            <td style="padding:10px 12px;border:1px solid #e5e7eb;text-align:right;">$${depositValue.toFixed(2)}</td>
          </tr>
          <tr>
            <td colspan="4" style="padding:10px 12px;border:1px solid #e5e7eb;text-align:right;">Saldo restante</td>
            <td style="padding:10px 12px;border:1px solid #e5e7eb;text-align:right;">$${remainingValue.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>

      ${notesHtml}
      ${imagesHtml}

      <div style="margin-top:24px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:14px 18px;">
        <p style="margin:0 0 6px;font-size:12px;font-weight:700;text-transform:uppercase;color:#1d4ed8;">Portal del cliente</p>
        <p style="margin:0;font-size:13px;color:#1f2937;">Una vez aprobada la propuesta, el cliente puede entrar a su cuenta desde:</p>
        <p style="margin:8px 0 0;font-size:13px;"><a href="${portalAccessUrl}" style="color:#2563eb;">${portalAccessUrl}</a></p>
      </div>

      <p style="margin-top:32px;font-size:11px;color:#9ca3af;text-align:center;border-top:1px solid #f3f4f6;padding-top:16px;">
        Carlo Rivera DBA BBTS · info@bbtspr.com · Esta propuesta fue generada el ${issueDate}
      </p>
    </div>`;
};

const downloadProposalPdf = async (proposal, client) => {
  const filename = `Propuesta-Carlo-Rivera-BBTS-${proposal.id?.slice(0, 8).toUpperCase() || 'PDF'}.pdf`;

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const convertImageToDataUrl = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl, {
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      return await blobToDataUrl(blob);
    } catch (error) {
      console.warn(`No se pudo preparar imagen para PDF: ${imageUrl}`, error);
      return null;
    }
  };

  const originalImages = getProposalImages(proposal);
  const resolvedImages = await Promise.all(originalImages.map(convertImageToDataUrl));
  const safeProposal = {
    ...proposal,
    images: resolvedImages.filter(Boolean),
  };

  try {
    const html = buildProposalHtml(safeProposal, client);
    await html2pdf()
      .set({
        margin: 10,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(html)
      .save();
  } catch (error) {
    console.error('Error generando PDF con imágenes, intentando fallback:', error);
    const fallbackHtml = buildProposalHtml({ ...proposal, images: [] }, client);
    await html2pdf()
      .set({
        margin: 10,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(fallbackHtml)
      .save();
  }
};

// ─── MODAL CREAR / EDITAR PROPUESTA ──────────────────────────────────────────
const ProposalFormModal = ({ client, editingProposal, onClose, onSaved, currentUser, userRole }) => {
  const [items, setItems] = useState(
    editingProposal?.items?.length
      ? editingProposal.items.map((item) => ({
          name: item.name || '',
          description: item.description || '',
          quantity: item.quantity || 1,
          price: item.price || '',
        }))
      : [{ ...emptyItem }]
  );
  const [deposit, setDeposit] = useState(editingProposal?.deposit ?? 0);
  const [clientNotes, setClientNotes] = useState(editingProposal?.clientNotes || editingProposal?.notes || '');
  const [imageUrlsText, setImageUrlsText] = useState(
    getProposalImages(editingProposal).join('\n')
  );
  const [saving, setSaving] = useState(false);

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const addItem = () => setItems([...items, { ...emptyItem }]);

  const removeItem = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated);
  };

  const total = items.reduce(
    (sum, item) => sum + parseFloat(item.price || 0) * parseInt(item.quantity || 1, 10),
    0
  );
  const depositNumber = parseFloat(deposit || 0);
  const remaining = total - depositNumber;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!client) return;
    setSaving(true);

    const clientInfo = {
      uid: client.uid,
      name: client.name,
      phone: client.phone,
      email: client.email,
      deposit: depositNumber,
    };
    const createdBy = {
      uid: currentUser?.uid || null,
      email: currentUser?.email || 'admin@bbtspr.com',
      role: userRole || 'admin',
      name: currentUser?.displayName || null,
    };
    const images = parseImageUrlsInput(imageUrlsText);
    const trimmedNotes = clientNotes.trim();

    try {
      if (editingProposal) {
        await updateDoc(doc(db, 'proposalTokens', editingProposal.id), {
          clientInfo,
          items,
          total,
          deposit: depositNumber,
          remaining,
          clientNotes: trimmedNotes,
          notes: trimmedNotes,
          images,
          portalLoginUrl: editingProposal.portalLoginUrl || PORTAL_LOGIN_URL,
          updatedAt: serverTimestamp(),
          updatedBy: createdBy,
        });
        await logClientActivity(client.uid, {
          summary: `Propuesta actualizada (#${editingProposal.id.slice(0, 6)})`,
          detail: `Total $${total.toFixed(2)}`,
          entityType: 'proposal',
          action: 'update',
          referenceId: editingProposal.id,
          createdBy: createdBy.email,
        });
      } else {
        const res = await fetch(
          'https://us-central1-tarjeta-ad945.cloudfunctions.net/sendProjectProposal',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              clientInfo,
              items,
              total,
              deposit: depositNumber,
              remaining,
              createdBy,
              clientNotes: trimmedNotes,
              images,
            }),
          }
        );
        const result = await res.json();
        if (!res.ok) {
          alert(`Error: ${result.error}`);
          setSaving(false);
          return;
        }
        await logClientActivity(client.uid, {
          summary: `Propuesta creada para ${client.name || client.email}`,
          detail: `Total $${total.toFixed(2)}`,
          entityType: 'proposal',
          action: 'create',
          createdBy: createdBy.email,
        });
      }
      onSaved();
    } catch (err) {
      console.error('Error al guardar propuesta:', err);
      alert('No se pudo guardar la propuesta.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4 py-6">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-xl overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-800">
            {editingProposal
              ? `Editando propuesta #${editingProposal.id.slice(0, 6)}`
              : 'Nueva Propuesta'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none font-light"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-6">
          {/* Items table */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Servicios Propuestos</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-100 rounded-xl overflow-hidden">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Servicio</th>
                    <th className="px-4 py-2 text-left">Descripción</th>
                    <th className="px-4 py-2 text-center w-20">Cant.</th>
                    <th className="px-4 py-2 text-right w-28">Precio</th>
                    <th className="px-4 py-2 text-right w-24">Total</th>
                    <th className="px-4 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="p-2">
                        <input
                          value={item.name}
                          onChange={(e) => handleItemChange(i, 'name', e.target.value)}
                          required
                          placeholder="Nombre del servicio"
                          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          value={item.description}
                          onChange={(e) => handleItemChange(i, 'description', e.target.value)}
                          required
                          placeholder="Descripción"
                          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(i, 'quantity', e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-orange-300"
                          min="1"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          value={item.price}
                          onChange={(e) => handleItemChange(i, 'price', e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-orange-300"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="p-2 text-right font-semibold text-gray-800">
                        ${(parseFloat(item.price || 0) * parseInt(item.quantity || 1, 10)).toFixed(2)}
                      </td>
                      <td className="p-2 text-center">
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(i)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={addItem}
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              <PlusIcon className="w-4 h-4" />
              Añadir servicio
            </button>
          </div>

          {/* Notas visibles para el cliente */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Notas para el cliente
              <span className="ml-2 text-xs font-normal text-gray-400">(aparecen en el email de la propuesta)</span>
            </label>
            <textarea
              value={clientNotes}
              onChange={(e) => setClientNotes(e.target.value)}
              placeholder="Ej: Esta propuesta incluye mantenimiento por 30 días. Válida hasta el 30 de mayo."
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Imágenes de la propuesta
              <span className="ml-2 text-xs font-normal text-gray-400">(una URL por línea o separadas por comas)</span>
            </label>
            <textarea
              value={imageUrlsText}
              onChange={(e) => setImageUrlsText(e.target.value)}
              placeholder="https://...\nhttps://..."
              rows={4}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 bg-gray-50 rounded-xl p-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Depósito</label>
              <input
                type="number"
                value={deposit}
                onChange={(e) => setDeposit(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Total</label>
              <input
                type="number"
                value={total.toFixed(2)}
                readOnly
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Saldo Restante</label>
              <input
                type="number"
                value={remaining.toFixed(2)}
                readOnly
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-xl text-sm border border-gray-200 text-gray-700 hover:bg-gray-50 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white px-6 py-2 rounded-xl text-sm font-semibold transition"
            >
              <PaperAirplaneIcon className="w-4 h-4" />
              {saving ? 'Guardando…' : editingProposal ? 'Guardar Cambios' : 'Enviar Propuesta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const AdminProposal = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const firebaseAuth = getAuth();
  const { userRole } = useAuth();

  const [client, setClient] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [commentsByProposal, setCommentsByProposal] = useState({});
  const [savingComment, setSavingComment] = useState(null);
  const [actionLoading, setActionLoading] = useState({ convert: null, remove: null });
  const [currentUser, setCurrentUser] = useState(() => firebaseAuth.currentUser);

  useEffect(() => {
    const unsub = firebaseAuth.onAuthStateChanged((user) => setCurrentUser(user));
    return () => unsub();
  }, [firebaseAuth]);

  const fetchProposals = async (clientEmail, clientUid) => {
    if (!clientEmail && !clientUid) return;
    setLoadingProposals(true);
    try {
      const queries = [];
      if (clientEmail) {
        queries.push(
          query(collection(db, 'proposalTokens'), where('clientInfo.email', '==', clientEmail.toLowerCase()))
        );
      }
      if (clientUid) {
        queries.push(
          query(collection(db, 'proposalTokens'), where('clientInfo.uid', '==', clientUid))
        );
      }
      const results = await Promise.all(queries.map((q) => getDocs(q)));
      const map = new Map();
      results.forEach((snap) => {
        snap.docs.forEach((docSnap) => {
          if (!map.has(docSnap.id)) map.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
      });
      const list = Array.from(map.values()).sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || a.createdAt || 0;
        const bDate = b.createdAt?.toDate?.() || b.createdAt || 0;
        return bDate - aDate;
      });
      setProposals(list);
    } catch (err) {
      console.error('Error al cargar propuestas:', err);
    } finally {
      setLoadingProposals(false);
    }
  };

  useEffect(() => {
    if (!clientId) return navigate('/admin/clients');
    const loadClient = async () => {
      try {
        const rawId = decodeURIComponent(clientId);
        const directRef = doc(db, 'clients', rawId);
        let snap = await getDoc(directRef);
        let resolvedId = directRef.id;

        if (!snap.exists()) {
          const emailCandidate = rawId.includes('@') ? rawId.toLowerCase() : null;
          let fallback = null;
          if (emailCandidate) {
            const emailSnap = await getDocs(
              query(collection(db, 'clients'), where('email', '==', emailCandidate), limit(1))
            );
            if (!emailSnap.empty) fallback = emailSnap.docs[0];
          }
          if (!fallback) {
            const uidSnap = await getDocs(
              query(collection(db, 'clients'), where('uid', '==', rawId), limit(1))
            );
            if (!uidSnap.empty) fallback = uidSnap.docs[0];
          }
          if (!fallback) throw new Error('Cliente no encontrado');
          snap = fallback;
          resolvedId = fallback.id;
        }

        const data = snap.data();
        setClient({
          uid: resolvedId,
          name: data.businessName || data.name || '',
          phone: data.phone || '',
          email: data.email || '',
        });
        await fetchProposals(data.email, resolvedId);
      } catch (err) {
        console.error('Error al cargar cliente:', err);
        navigate('/admin/clients');
      }
    };
    loadClient();
  }, [clientId, navigate]);

  const handleAddComment = async (proposalId) => {
    const text = (commentsByProposal[proposalId] || '').trim();
    if (!text) return;
    setSavingComment(proposalId);
    try {
      const comment = {
        id: Date.now().toString(),
        text,
        createdBy: currentUser?.email || 'admin',
        createdAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, 'proposalTokens', proposalId), {
        comments: arrayUnion(comment),
      });
      setProposals((prev) =>
        prev.map((p) =>
          p.id === proposalId ? { ...p, comments: [...(p.comments || []), comment] } : p
        )
      );
      setCommentsByProposal((prev) => ({ ...prev, [proposalId]: '' }));
    } catch (err) {
      console.error('Error al agregar comentario:', err);
      alert('No se pudo agregar el comentario.');
    } finally {
      setSavingComment(null);
    }
  };

  const handleConvertToInvoice = async (proposal) => {
    if (!proposal) return;
    const clientUid = proposal.clientInfo?.uid || client?.uid;
    if (!clientUid) { alert('No se encontró el ID del cliente.'); return; }
    setActionLoading((prev) => ({ ...prev, convert: proposal.id }));
    try {
      const proposalItems = proposal.items || [];
      const total = proposalItems.reduce(
        (sum, item) => sum + parseFloat(item.quantity || 0) * parseFloat(item.price || 0),
        0
      );
      const depositValue = parseFloat(proposal.deposit || 0);
      await addDoc(collection(db, 'clients', clientUid, 'invoices'), {
        clientId: clientUid,
        clientEmail: proposal.clientInfo?.email?.toLowerCase?.() || client?.email || '',
        clientName: proposal.clientInfo?.name || client?.name || '',
        items: proposalItems,
        total,
        deposit: depositValue,
        discount: 0,
        payments: [],
        remaining: total - depositValue,
        status: 'pendiente',
        notes: `Generado desde propuesta #${proposal.id.slice(0, 6)}`,
        createdAt: serverTimestamp(),
        createdBy: {
          uid: currentUser?.uid || null,
          email: currentUser?.email || 'admin@bbtspr.com',
          role: userRole || 'admin',
        },
        sellerId: proposal.createdBy?.uid || null,
      });
      await updateDoc(doc(db, 'proposalTokens', proposal.id), {
        status: 'convertido',
        convertedToInvoiceAt: serverTimestamp(),
      });
      await logClientActivity(clientUid, {
        summary: 'Propuesta convertida en factura',
        detail: `#${proposal.id.slice(0, 6)} → $${total.toFixed(2)}`,
        entityType: 'proposal',
        action: 'convert',
        referenceId: proposal.id,
        createdBy: currentUser?.email || 'admin',
      });
      alert('Propuesta convertida en factura. Revisa la pestaña "Facturas".');
      await fetchProposals(client?.email, clientUid);
      setExpandedId(null);
    } catch (err) {
      console.error('❌ Error al convertir propuesta:', err);
      alert('No se pudo convertir la propuesta.');
    } finally {
      setActionLoading((prev) => ({ ...prev, convert: null }));
    }
  };

  const handleResendProposal = async (proposal) => {
    if (!window.confirm(`¿Reenviar esta propuesta por email y SMS a ${proposal.clientInfo?.email}?`)) return;
    setActionLoading((prev) => ({ ...prev, resend: proposal.id }));
    try {
      const res = await fetch(
        'https://us-central1-tarjeta-ad945.cloudfunctions.net/resendProjectProposal',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ proposalId: proposal.id }),
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Error desconocido');
      alert('Propuesta reenviada por email y SMS.');
      await fetchProposals(client?.email, client?.uid);
    } catch (err) {
      console.error('Error al reenviar propuesta:', err);
      alert(`No se pudo reenviar: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, resend: null }));
    }
  };

  const handleDeleteProposal = async (proposal) => {
    if (!proposal?.id) return;
    if (!window.confirm('¿Eliminar esta propuesta? Esta acción no se puede deshacer.')) return;
    setActionLoading((prev) => ({ ...prev, remove: proposal.id }));
    try {
      await deleteDoc(doc(db, 'proposalTokens', proposal.id));
      setProposals((prev) => prev.filter((p) => p.id !== proposal.id));
      if (expandedId === proposal.id) setExpandedId(null);
      await logClientActivity(proposal.clientInfo?.uid || client?.uid, {
        summary: 'Propuesta eliminada',
        entityType: 'proposal',
        action: 'delete',
        referenceId: proposal.id,
        createdBy: currentUser?.email || 'admin',
      });
    } catch (err) {
      console.error('❌ Error eliminando propuesta:', err);
      alert('No se pudo eliminar la propuesta.');
    } finally {
      setActionLoading((prev) => ({ ...prev, remove: null }));
    }
  };

  if (!client) return <p className="text-center text-gray-500 py-10">Cargando cliente…</p>;

  const activeProposals = proposals.filter((p) => (p.status || 'pendiente') !== 'convertido');
  const convertedProposals = proposals.filter((p) => p.status === 'convertido');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <InformationCircleIcon className="h-6 w-6 text-blue-500 flex-shrink-0" />
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Propuestas</h2>
            <p className="text-sm text-gray-500">{client.name} · {client.email}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setEditingProposal(null); setShowFormModal(true); }}
          className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow transition flex-shrink-0"
        >
          <PlusIcon className="w-4 h-4" />
          Nueva Propuesta
        </button>
      </div>

      {/* Propuestas activas */}
      {loadingProposals ? (
        <p className="text-sm text-gray-400">Cargando propuestas…</p>
      ) : activeProposals.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-dashed border-gray-200">
          <p className="text-gray-400 text-sm">Este cliente no tiene propuestas activas.</p>
          <button
            type="button"
            onClick={() => { setEditingProposal(null); setShowFormModal(true); }}
            className="mt-3 text-orange-600 text-sm font-semibold hover:underline"
          >
            Crear la primera propuesta →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {activeProposals.map((proposal) => {
            const createdAt =
              proposal.createdAt?.toDate?.() ||
              (proposal.createdAt ? new Date(proposal.createdAt) : null);
            const totalValue = parseFloat(proposal.total || 0);
            const depositValue = parseFloat(proposal.deposit || 0);
            const remainingValue =
              proposal.remaining !== undefined
                ? parseFloat(proposal.remaining)
                : totalValue - depositValue;
            const isExpanded = expandedId === proposal.id;
            const status = proposal.status || 'pendiente';
            const statusStyle = STATUS_STYLES[status] || 'bg-gray-100 text-gray-600';
            const statusLabel = STATUS_LABELS[status] || status;
            const comments = proposal.comments || [];
            const commentInput = commentsByProposal[proposal.id] || '';
            const proposalNotes = getProposalNotes(proposal);
            const proposalImages = getProposalImages(proposal);
            const accessUrl = proposal.accountAccessUrl || proposal.portalLoginUrl || PORTAL_LOGIN_URL;
            const accessLabel = proposal.accountAccessLabel
              || (proposal.hasExistingAccount ? 'Entrar al portal' : 'Crear contraseña');

            return (
              <div
                key={proposal.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
              >
                {/* Fila resumen (clickable para expandir) */}
                <div
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 cursor-pointer hover:bg-orange-50/40 transition-colors select-none"
                  onClick={() => setExpandedId(isExpanded ? null : proposal.id)}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusStyle}`}>
                      {statusLabel}
                    </span>
                    <span className="text-xs text-gray-400 italic">
                      {isExpanded ? 'Clic para cerrar' : 'Clic para ver detalle y comentarios'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {createdAt ? createdAt.toLocaleDateString('es-PR') : '—'}
                    </span>
                    {comments.length > 0 && (
                      <span className="flex items-center gap-1 text-xs text-indigo-600 font-medium">
                        <ChatBubbleLeftEllipsisIcon className="w-3.5 h-3.5" />
                        {comments.length} comentario{comments.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Total</p>
                      <p className="text-sm font-bold text-gray-800">${totalValue.toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Depósito</p>
                      <p className="text-sm text-gray-600">${depositValue.toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Saldo</p>
                      <p className="text-sm text-gray-600">${remainingValue.toFixed(2)}</p>
                    </div>
                    {isExpanded ? (
                      <ChevronUpIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                </div>

                {/* Detalle expandido */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-5 py-5 space-y-6">
                    {/* Servicios */}
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                        Servicios
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-gray-700">
                          <thead className="text-xs uppercase text-gray-400 border-b border-gray-100">
                            <tr>
                              <th className="py-2 text-left pr-4">Servicio</th>
                              <th className="py-2 text-left pr-4">Descripción</th>
                              <th className="py-2 text-center pr-4">Cant.</th>
                              <th className="py-2 text-right pr-4">Precio</th>
                              <th className="py-2 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(proposal.items || []).map((item, i) => (
                              <tr key={i} className="border-b border-gray-50 last:border-0">
                                <td className="py-2 pr-4 font-medium">{item.name}</td>
                                <td className="py-2 pr-4 text-gray-500">{item.description}</td>
                                <td className="py-2 pr-4 text-center">{item.quantity}</td>
                                <td className="py-2 pr-4 text-right">
                                  ${parseFloat(item.price || 0).toFixed(2)}
                                </td>
                                <td className="py-2 text-right font-semibold">
                                  ${(parseFloat(item.price || 0) * parseInt(item.quantity || 1, 10)).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Notas del cliente */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                      <p className="text-xs font-semibold uppercase text-amber-700 mb-1">
                        Notas enviadas al cliente
                      </p>
                      <p className="text-sm text-gray-800 whitespace-pre-line">
                        {proposalNotes || 'Sin notas registradas para esta propuesta.'}
                      </p>
                    </div>

                    {proposalImages.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                          Imágenes de referencia
                        </h4>
                        <div className="grid gap-3 sm:grid-cols-2">
                          {proposalImages.map((imageUrl, index) => (
                            <a
                              key={`${proposal.id}-image-${index}`}
                              href={imageUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="block overflow-hidden rounded-xl border border-gray-200 bg-gray-50"
                            >
                              <img
                                src={imageUrl}
                                alt={`Propuesta ${proposal.id} imagen ${index + 1}`}
                                className="h-48 w-full object-cover"
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="bg-sky-50 border border-sky-200 rounded-xl px-4 py-3">
                      <p className="text-xs font-semibold uppercase text-sky-700 mb-1">
                        Acceso del cliente
                      </p>
                      <p className="text-sm text-gray-700">
                        {proposal.hasExistingAccount
                          ? 'El cliente ya tiene cuenta activa y puede entrar desde su portal.'
                          : 'Se preparó un enlace para que el cliente cree su contraseña y entre a su cuenta.'}
                      </p>
                      <div className="mt-3">
                        <a
                          href={accessUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-sky-700"
                        >
                          {accessLabel}
                        </a>
                      </div>
                    </div>

                    {/* Comentarios */}
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3 flex items-center gap-1.5">
                        <ChatBubbleLeftEllipsisIcon className="w-4 h-4" />
                        Comentarios internos ({comments.length})
                      </h4>
                      {comments.length > 0 && (
                        <ul className="space-y-2 mb-3">
                          {comments.map((c) => (
                            <li
                              key={c.id || c.createdAt}
                              className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3"
                            >
                              <p className="text-sm text-gray-800">{c.text}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {c.createdBy} ·{' '}
                                {c.createdAt
                                  ? new Date(c.createdAt).toLocaleString('es-PR')
                                  : '—'}
                              </p>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={commentInput}
                          onChange={(e) =>
                            setCommentsByProposal((prev) => ({
                              ...prev,
                              [proposal.id]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleAddComment(proposal.id);
                            }
                          }}
                          placeholder="Añade un comentario interno… (Enter para enviar)"
                          className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                        />
                        <button
                          type="button"
                          disabled={!commentInput.trim() || savingComment === proposal.id}
                          onClick={() => handleAddComment(proposal.id)}
                          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-sm font-medium transition"
                        >
                          {savingComment === proposal.id ? '…' : 'Agregar'}
                        </button>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => handleResendProposal(proposal)}
                        disabled={actionLoading.resend === proposal.id}
                        className="inline-flex items-center gap-1.5 text-sm bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 px-4 py-2 rounded-xl font-medium transition"
                      >
                        <PaperAirplaneIcon className="w-4 h-4" />
                        {actionLoading.resend === proposal.id ? 'Enviando…' : 'Reenviar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingProposal(proposal);
                          setShowFormModal(true);
                        }}
                        className="inline-flex items-center gap-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 px-4 py-2 rounded-xl font-medium transition"
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                        Editar propuesta
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadProposalPdf(proposal, client)}
                        className="inline-flex items-center gap-1.5 text-sm bg-gray-50 text-gray-700 hover:bg-gray-100 px-4 py-2 rounded-xl font-medium transition"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        Descargar PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => handleConvertToInvoice(proposal)}
                        disabled={actionLoading.convert === proposal.id}
                        className="inline-flex items-center gap-1.5 text-sm bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50 px-4 py-2 rounded-xl font-medium transition"
                      >
                        {actionLoading.convert === proposal.id
                          ? 'Convirtiendo…'
                          : '→ Convertir a factura'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteProposal(proposal)}
                        disabled={actionLoading.remove === proposal.id}
                        className="inline-flex items-center gap-1.5 text-sm bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50 px-4 py-2 rounded-xl font-medium transition"
                      >
                        <TrashIcon className="w-4 h-4" />
                        {actionLoading.remove === proposal.id ? 'Eliminando…' : 'Eliminar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Propuestas ya convertidas a factura (sección separada, dimmed) */}
      {convertedProposals.length > 0 && (
        <div className="space-y-2 pt-2">
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
              Convertidas a factura ({convertedProposals.length})
            </span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
          {convertedProposals.map((proposal) => {
            const createdAt =
              proposal.createdAt?.toDate?.() ||
              (proposal.createdAt ? new Date(proposal.createdAt) : null);
            const totalValue = parseFloat(proposal.total || 0);
            return (
              <div
                key={proposal.id}
                className="bg-gray-50 rounded-xl border border-gray-100 px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 opacity-60"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold bg-gray-200 text-gray-500 px-2.5 py-1 rounded-full">
                    Factura generada
                  </span>
                  <span className="text-sm text-gray-400">
                    {createdAt ? createdAt.toLocaleDateString('es-PR') : '—'}
                  </span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Total</p>
                    <p className="text-sm font-semibold text-gray-500">${totalValue.toFixed(2)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteProposal(proposal)}
                    disabled={actionLoading.remove === proposal.id}
                    className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50"
                  >
                    {actionLoading.remove === proposal.id ? 'Eliminando…' : 'Eliminar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal crear / editar */}
      {showFormModal && (
        <ProposalFormModal
          client={client}
          editingProposal={editingProposal}
          currentUser={currentUser}
          userRole={userRole}
          onClose={() => {
            setShowFormModal(false);
            setEditingProposal(null);
          }}
          onSaved={async () => {
            setShowFormModal(false);
            setEditingProposal(null);
            await fetchProposals(client.email, client.uid);
          }}
        />
      )}
    </div>
  );
};

export default AdminProposal;
